/**
 * @file catalog.ts
 * @description Заготовки секций платформы (phase19.16 Р2; SB27; uiux-design-r2
 * §5 — состав Р2). Нейтральные: без цветов магазина (только роли), без
 * картинок (места под фото пустые — `mediaId: null`, SB21, SB97), без названий
 * магазина; тексты — общие заглушки. Элементы с данными берут их из каталога,
 * блога, отзывов и настроек сайта (SB25).
 *
 * Новая заготовка — новая запись (OCP); тест проверяет, что каждая проходит
 * `validateSectionPayload`.
 */

import { defaultSectionLook } from "../defaults.js";
import { catalogButton, el, eyebrow, header, heading, photo, section, text } from "./build.js";
import { GENERAL_PRESET_CONTEXTS, type SectionPreset } from "./types.js";

const HERO_TITLE = "Заголовок первого экрана";
const ABOUT_TEXT = "Короткий текст о том, чем вы занимаетесь.";
const center = { align: "center" as const };

/** Одна запись реестра: previewKey = key, по умолчанию без фото-фона и условий. */
function preset(
  p: Omit<SectionPreset, "previewKey" | "backgroundPhoto" | "requires" | "contexts"> & Partial<SectionPreset>,
): SectionPreset {
  return { previewKey: p.key, backgroundPhoto: false, requires: null, contexts: GENERAL_PRESET_CONTEXTS, ...p };
}

const HERO: SectionPreset[] = [
  preset({
    key: "hero-photo-right-bleed",
    group: "hero",
    title: "Фото справа в край",
    hint: "Главная страница",
    build: () =>
      section({
        // На телефоне фото первым (uiux-design-r2 §1 п.4: «Сначала фото»).
        phoneOrder: [2, 1],
        // «Первый экран»: 100 % окна без шапки; телефон — как на ПК (вопрос 1, 2026-10-18).
        look: { ...defaultSectionLook(), height: { kind: "screen", value: 100 }, container: "full" },
        columns: [
          [eyebrow("Надпись над заголовком"), heading(1, HERO_TITLE), text(ABOUT_TEXT), catalogButton("Перейти в каталог", "primary", true)],
          [photo("bleed")],
        ],
      }),
  }),
  preset({
    key: "hero-photo-full",
    group: "hero",
    title: "Фото на весь экран",
    hint: "Баннер, акция",
    backgroundPhoto: true,
    build: () =>
      section({
        look: {
          ...defaultSectionLook(),
          // Пока фото не выбрано — тёмная роль; кабинет сразу предлагает медиатеку.
          bg: { kind: "role", role: "darkBg" },
          container: "full",
          height: { kind: "screen", value: 100 },
        },
        columns: [
          [
            el("eyebrow", { text: "Надпись над заголовком", preset: "caps", ...center }),
            el("heading", { level: 1, text: HERO_TITLE, ...center }),
            el("text", { text: ABOUT_TEXT, ...center }),
            el("button", { label: "Подробнее", target: { type: "system", route: "CATALOG" }, inline: true, ...center }),
          ],
        ],
      }),
  }),
  preset({
    key: "hero-text-center-color",
    group: "hero",
    title: "Текст по центру на цвете",
    hint: "Внутренние страницы",
    build: () =>
      section({
        look: { ...defaultSectionLook(), bg: { kind: "role", role: "panelBg" } },
        columns: [
          [
            el("eyebrow", { text: "Раздел", preset: "caps", ...center }),
            el("heading", { level: 1, text: "Заголовок страницы", ...center }),
            el("text", { text: "Пара предложений о том, что здесь найдёт покупатель.", ...center }),
          ],
        ],
      }),
  }),
  preset({
    key: "hero-photo-left",
    group: "hero",
    title: "Фото слева, текст справа",
    hint: "О товаре, о мастерской",
    build: () =>
      section({
        columns: [
          [photo("fit")],
          [eyebrow("Надпись над заголовком"), heading(1, HERO_TITLE), text(ABOUT_TEXT), catalogButton("Перейти в каталог", "primary", true)],
        ],
      }),
  }),
];

const TILES: SectionPreset[] = [
  preset({
    key: "tiles-3-categories",
    group: "tiles",
    title: "Три плитки категорий",
    hint: "Разделы каталога на главной",
    build: () =>
      section({
        header: header("Категории"),
        columns: [[el("categories", { source: "auto", limit: 3, cols: 3, look: "overlay", phoneLayout: "grid2" })]],
      }),
  }),
  preset({
    key: "tiles-4",
    group: "tiles",
    title: "Плитки-ссылки 4 в ряд",
    hint: "Подборки, разделы, акции",
    build: () =>
      section({
        columns: [
          [
            el("tiles", {
              cols: 4,
              look: "below",
              phoneLayout: "grid2",
              items: [1, 2, 3, 4].map(() => ({ mediaId: null, title: "Название", target: null })),
            }),
          ],
        ],
      }),
  }),
];

const PRODUCTS: SectionPreset[] = [
  preset({
    key: "products-new-with-header",
    group: "products",
    title: "Новинки с линией и ссылкой",
    hint: "Свежие товары на главной",
    build: () =>
      section({
        header: header("Новинки", { line: true, linkLabel: "Все товары" }),
        columns: [[el("products", { source: "new", limit: 4, perRow: 4 })]],
      }),
  }),
  preset({
    key: "products-badge",
    group: "products",
    title: "Товары с бейджем",
    hint: "Бестселлеры или распродажа",
    build: () =>
      section({
        header: header("Бестселлеры"),
        columns: [[el("products", { source: "badge", badge: "BESTSELLER", limit: 4, perRow: 4 })]],
      }),
  }),
  preset({
    key: "promotions-cards",
    group: "products",
    title: "Акции карточками",
    hint: "Действующие акции магазина на главной",
    build: () =>
      section({
        header: header("Акции"),
        // Акции из раздела «Акции» (SB25); нет акций — секция скрыта (SB21).
        columns: [[el("announcements", { source: "promotions", items: [], layout: "cards", perRow: 3, limit: 3 })]],
      }),
  }),
];

const TEXT_PHOTO: SectionPreset[] = [
  preset({
    key: "text-photo-card",
    group: "text-photo",
    title: "Текст и фото в карточке",
    hint: "О мастерской, о бренде",
    build: () =>
      section({
        look: { ...defaultSectionLook(), card: { on: true } },
        columns: [[heading(2, "Заголовок раздела"), text(ABOUT_TEXT), catalogButton("Подробнее", "link")], [photo("column")]],
      }),
  }),
  preset({
    key: "text-photo-plain",
    group: "text-photo",
    title: "Текст и фото без рамки",
    hint: "Рассказ о товаре или процессе",
    build: () =>
      section({
        columns: [[photo("fit")], [heading(2, "Заголовок раздела"), text(ABOUT_TEXT)]],
      }),
  }),
];

export const SECTION_PRESETS_HERO_TO_TEXT: readonly SectionPreset[] = [...HERO, ...TILES, ...PRODUCTS, ...TEXT_PHOTO];
